import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Pill,
  Plus,
  Edit,
  Trash2,
  X,
  Check,
  CheckCircle2
} from 'lucide-react';
import PageHeader from '../components/PageHeader';
import toast from 'react-hot-toast';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

// Helper functions for date conversion
const formatDateToDisplay = (isoDate: string): string => {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatDateToISO = (displayDate: string): string => {
  if (!displayDate) return '';
  const parts = displayDate.split('/');
  if (parts.length !== 3) return '';
  const [day, month, year] = parts;
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
};

// Auto-format date input as user types (DD/MM/YYYY)
const handleDateInput = (value: string): string => {
  // Remove all non-digits
  const digits = value.replace(/\D/g, '');
  
  // Format as DD/MM/YYYY
  if (digits.length <= 2) {
    return digits;
  } else if (digits.length <= 4) {
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  } else {
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
  }
};

interface Medication {
  id: string;
  name: string;
  dose: string;
  frequency: string;
  prescribedBy?: string;
  startDate?: string;
  endDate?: string;
  notes?: string;
  isActive: boolean;
}

export default function MedicationsPage() {
  const [medications, setMedications] = useState<Medication[]>([]);
  const [loading, setLoading] = useState(true);
  const [loggingMedId, setLoggingMedId] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingMed, setEditingMed] = useState<Medication | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    dose: '',
    frequency: '',
    prescribedBy: '',
    startDate: '',
    endDate: '',
    notes: ''
  });
  const [errors, setErrors] = useState({
    name: '',
    dose: '',
    frequency: ''
  });

  useEffect(() => {
    loadMedications();
  }, []);

  const loadMedications = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API_URL}/api/profile/medications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMedications(response.data);
    } catch (error: any) {
      console.error('Error loading medications:', error);
      if (error.response?.status === 429) {
        toast.error('Prea multe cereri. Te rog așteaptă câteva secunde.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingMed(null);
    setFormData({
      name: '',
      dose: '',
      frequency: '',
      prescribedBy: '',
      startDate: '',
      endDate: '',
      notes: ''
    });
    setShowModal(true);
  };

  const handleEdit = (med: Medication) => {
    setEditingMed(med);
    setFormData({
      name: med.name,
      dose: med.dose,
      frequency: med.frequency,
      prescribedBy: med.prescribedBy || '',
      startDate: med.startDate ? formatDateToDisplay(med.startDate) : '',
      endDate: med.endDate ? formatDateToDisplay(med.endDate) : '',
      notes: med.notes || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Sigur vrei să ștergi acest medicament?')) return;

    try {
      const token = localStorage.getItem('accessToken');
      await axios.delete(`${API_URL}/api/profile/medications/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadMedications();
    } catch (error) {
      console.error('Error deleting medication:', error);
    }
  };

  const handleQuickLog = async (medicationId: string, medicationName: string) => {
    setLoggingMedId(medicationId);
    
    try {
      const token = localStorage.getItem('accessToken');
      
      await axios.post(
        `${API_URL}/api/tracking/medications`,
        {
          medicationId,
          takenAt: new Date().toISOString(),
          skipped: false,
          notes: ''
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`${medicationName} înregistrat cu succes!`, {
        duration: 3000,
        position: 'top-center',
        style: {
          background: '#10b981',
          color: '#fff',
          fontWeight: 'bold'
        }
      });
    } catch (error: any) {
      console.error('Error logging medication:', error);
      toast.error(error.response?.data?.error || '❌ Eroare la înregistrare. Te rog reîncearcă.', {
        duration: 4000,
        position: 'top-center'
      });
    } finally {
      setLoggingMedId(null);
    }
  };

  const validateForm = (): boolean => {
    const newErrors = {
      name: '',
      dose: '',
      frequency: ''
    };

    let isValid = true;

    if (!formData.name.trim()) {
      newErrors.name = 'Numele medicamentului este obligatoriu';
      isValid = false;
    }

    if (!formData.dose.trim()) {
      newErrors.dose = 'Doza este obligatorie';
      isValid = false;
    }

    if (!formData.frequency.trim()) {
      newErrors.frequency = 'Frecvența este obligatorie';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      toast.error('Te rog completează toate câmpurile obligatorii (*)');
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');
      
      // Convert dates to ISO format for backend, but only if provided
      const startDateISO = formData.startDate ? formatDateToISO(formData.startDate) : undefined;
      const endDateISO = formData.endDate ? formatDateToISO(formData.endDate) : undefined;
      
      const dataToSend = {
        name: formData.name,
        dose: formData.dose,
        frequency: formData.frequency,
        prescribedBy: formData.prescribedBy || undefined,
        startDate: startDateISO,
        endDate: endDateISO,
        notes: formData.notes || undefined
      };
      
      if (editingMed) {
        // Update
        await axios.put(
          `${API_URL}/api/profile/medications/${editingMed.id}`,
          dataToSend,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Medicament actualizat cu succes!');
      } else {
        // Create
        await axios.post(
          `${API_URL}/api/profile/medications`,
          dataToSend,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Medicament adăugat cu succes!');
      }

      setShowModal(false);
      setErrors({ name: '', dose: '', frequency: '' });
      loadMedications();
    } catch (error) {
      console.error('Error saving medication:', error);
      toast.error('Eroare la salvarea medicamentului');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <PageHeader
        title="Medicamentele Mele"
        subtitle="Tratamente și medicamente curente"
        showBack={true}
        backTo="/profile"
        icon={<Pill className="w-5 h-5" />}
        actions={
          <button
            onClick={handleAdd}
            className="flex items-center px-3 py-2 sm:px-6 sm:py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm sm:text-base"
          >
            <Plus className="w-4 h-4 sm:w-5 sm:h-5 sm:mr-2" />
            <span className="hidden sm:inline">Adaugă</span>
          </button>
        }
      />

      <div className="max-w-4xl mx-auto px-4 py-6">

        {/* Medications List */}
        {medications.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-12 text-center">
            <Pill className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Nu ai medicamente adăugate
            </h3>
            <p className="text-gray-600 mb-6">
              Adaugă medicamentele pe care le iei pentru a le urmări mai ușor
            </p>
            <button
              onClick={handleAdd}
              className="inline-flex items-center px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              <Plus className="w-5 h-5 mr-2" />
              Adaugă primul medicament
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {medications.map((med) => (
              <div key={med.id} className="bg-white rounded-2xl shadow-xl p-4 sm:p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                        <Pill className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">{med.name}</h3>
                        <p className="text-sm text-gray-600">{med.dose}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <div>
                        <p className="text-xs text-gray-500">Frecvență</p>
                        <p className="text-sm font-medium text-gray-900">{med.frequency}</p>
                      </div>

                      {med.prescribedBy && (
                        <div>
                          <p className="text-xs text-gray-500">Prescris de</p>
                          <p className="text-sm font-medium text-gray-900">{med.prescribedBy}</p>
                        </div>
                      )}

                      {med.startDate && (
                        <div>
                          <p className="text-xs text-gray-500">Data început</p>
                          <p className="text-sm font-medium text-gray-900">
                            {new Date(med.startDate).toLocaleDateString('ro-RO')}
                          </p>
                        </div>
                      )}

                      {med.endDate && (
                        <div>
                          <p className="text-xs text-gray-500">Data sfârșit</p>
                          <p className="text-sm font-medium text-gray-900">
                            {new Date(med.endDate).toLocaleDateString('ro-RO')}
                          </p>
                        </div>
                      )}
                    </div>

                    {med.notes && (
                      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500 mb-1">Notițe</p>
                        <p className="text-sm text-gray-700">{med.notes}</p>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-2 ml-4">
                    <div>
                      <button
                        onClick={() => handleQuickLog(med.id, med.name)}
                        disabled={loggingMedId === med.id}
                        className={`flex items-center px-3 py-2 rounded-lg transition text-sm font-medium ${
                          loggingMedId === med.id
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-green-600 text-white hover:bg-green-700'
                        }`}
                        title="Salvează în calendar"
                      >
                        {loggingMedId === med.id ? (
                          <>
                            <div className="w-4 h-4 mr-1 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            Se salvează...
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                            Am luat
                          </>
                        )}
                      </button>
                      <p className="text-xs text-gray-500 text-center mt-1">→ adaugă în calendar</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(med)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                        title="Editează"
                      >
                        <Edit className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(med.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        title="Șterge"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal Add/Edit */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingMed ? 'Editează medicament' : 'Adaugă medicament'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nume medicament *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({...formData, name: e.target.value});
                    if (errors.name) setErrors({...errors, name: ''});
                  }}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 ${
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Ex: Ibuprofen"
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Doza *
                </label>
                <input
                  type="text"
                  value={formData.dose}
                  onChange={(e) => {
                    setFormData({...formData, dose: e.target.value});
                    if (errors.dose) setErrors({...errors, dose: ''});
                  }}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 ${
                    errors.dose ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Ex: 400mg"
                />
                {errors.dose && (
                  <p className="text-red-500 text-sm mt-1">{errors.dose}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Frecvență *
                </label>
                <input
                  type="text"
                  value={formData.frequency}
                  onChange={(e) => {
                    setFormData({...formData, frequency: e.target.value});
                    if (errors.frequency) setErrors({...errors, frequency: ''});
                  }}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 ${
                    errors.frequency ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Ex: 2 ori pe zi după mâncare"
                />
                {errors.frequency && (
                  <p className="text-red-500 text-sm mt-1">{errors.frequency}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prescris de
                </label>
                <input
                  type="text"
                  value={formData.prescribedBy}
                  onChange={(e) => setFormData({...formData, prescribedBy: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="Ex: Dr. Popescu"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Data început
                  </label>
                  <input
                    type="text"
                    value={formData.startDate}
                    onChange={(e) => setFormData({...formData, startDate: handleDateInput(e.target.value)})}
                    placeholder="zi/luna/an"
                    maxLength={10}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Format: zi/luna/an - 10/01/2020</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Data sfârșit (opțional)
                  </label>
                  <input
                    type="text"
                    value={formData.endDate}
                    onChange={(e) => setFormData({...formData, endDate: handleDateInput(e.target.value)})}
                    placeholder="zi/luna/an"
                    maxLength={10}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Format: zi/luna/an - 15/03/2021</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notițe
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="Ex: Se ia cu apă, nu pe stomacul gol"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Anulează
                </button>
                <button
                  type="submit"
                  className="flex items-center px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  <Check className="w-5 h-5 mr-2" />
                  {editingMed ? 'Actualizează' : 'Adaugă'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
